import React, { useState } from "react";
import clsx from "clsx";
import { Button, Popover, IconButton } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DateRangeIcon from "@material-ui/icons/DateRange";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isWithinInterval,
  addMonths,
  subMonths,
  isBefore,
  subDays,
} from "date-fns";
import { ptBR } from "date-fns/locale";

const useStyles = makeStyles((theme) => ({
  trigger: {
    width: 170,
    height: 40,
    textTransform: "none",
    borderRadius: 8,
    fontWeight: 400,
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
    justifyContent: "flex-start",
    backgroundColor: theme.palette.background.paper,
    padding: "0 12px",
    "& .MuiButton-startIcon": {
      color: theme.palette.action.active,
      marginRight: 6,
    },
  },
  pop: { display: "flex", padding: 10, borderRadius: 12 },
  presets: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    paddingRight: 10,
    marginRight: 10,
    borderRight: `1px solid ${theme.palette.divider}`,
    minWidth: 150,
  },
  preset: {
    padding: "8px 12px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: "0.85rem",
    fontWeight: 500,
    color: theme.palette.text.primary,
    "&:hover": { backgroundColor: "rgba(13,148,136,0.12)", color: "#0D9488" },
  },
  calWrap: { width: 264 },
  calHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  calTitle: {
    fontWeight: 600,
    fontSize: "0.9rem",
    textTransform: "capitalize",
  },
  grid: { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 },
  dow: {
    textAlign: "center",
    fontSize: "0.68rem",
    color: theme.palette.text.secondary,
    fontWeight: 700,
    padding: "4px 0",
  },
  day: {
    textAlign: "center",
    fontSize: "0.8rem",
    padding: "7px 0",
    borderRadius: 6,
    cursor: "pointer",
    "&:hover": { backgroundColor: "rgba(13,148,136,0.15)" },
  },
  dayMuted: { color: theme.palette.text.disabled },
  dayRange: { backgroundColor: "rgba(13,148,136,0.15)" },
  daySel: {
    backgroundColor: "#0D9488",
    color: "#fff",
    fontWeight: 700,
    "&:hover": { backgroundColor: "#0f766e" },
  },
}));

const iso = (d) => format(d, "yyyy-MM-dd");
const br = (d) => format(d, "dd/MM");

const PeriodFilter = ({ value, onChange }) => {
  const classes = useStyles();
  const [anchor, setAnchor] = useState(null);
  const [viewDate, setViewDate] = useState(new Date());
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);

  const open = Boolean(anchor);

  const apply = (from, to, label) => {
    onChange({
      dateFrom: from ? iso(from) : "",
      dateTo: to ? iso(to) : "",
      label,
    });
    setAnchor(null);
  };

  const presets = [
    { label: "Hoje", fn: () => { const t = new Date(); return [t, t]; } },
    { label: "Ontem", fn: () => { const y = subDays(new Date(), 1); return [y, y]; } },
    { label: "Últimos 7 dias", fn: () => [subDays(new Date(), 6), new Date()] },
    { label: "Últimos 30 dias", fn: () => [subDays(new Date(), 29), new Date()] },
    { label: "Este mês", fn: () => [startOfMonth(new Date()), new Date()] },
    {
      label: "Mês passado",
      fn: () => {
        const p = subMonths(new Date(), 1);
        return [startOfMonth(p), endOfMonth(p)];
      },
    },
    { label: "Tudo", fn: () => [null, null] },
  ];

  const handlePreset = (p) => {
    const [f, t] = p.fn();
    setStart(f);
    setEnd(t);
    apply(f, t, p.label);
  };

  const handleDayClick = (day) => {
    if (!start || (start && end)) {
      setStart(day);
      setEnd(null);
    } else {
      let f = start;
      let t = day;
      if (isBefore(day, start)) {
        f = day;
        t = start;
      }
      setStart(f);
      setEnd(t);
      apply(f, t, `${br(f)} → ${br(t)}`);
    }
  };

  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(viewDate), { weekStartsOn: 0 }),
    end: endOfWeek(endOfMonth(viewDate), { weekStartsOn: 0 }),
  });

  const inRange = (day) =>
    start && end && isWithinInterval(day, { start, end });

  const label = value && value.label ? value.label : "Período";

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        className={classes.trigger}
        startIcon={<DateRangeIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        {label}
      </Button>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: classes.pop }}
      >
        <div className={classes.presets}>
          {presets.map((p) => (
            <div key={p.label} className={classes.preset} onClick={() => handlePreset(p)}>
              {p.label}
            </div>
          ))}
        </div>
        <div className={classes.calWrap}>
          <div className={classes.calHead}>
            <IconButton size="small" onClick={() => setViewDate(subMonths(viewDate, 1))}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <span className={classes.calTitle}>
              {format(viewDate, "MMMM yyyy", { locale: ptBR })}
            </span>
            <IconButton size="small" onClick={() => setViewDate(addMonths(viewDate, 1))}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </div>
          <div className={classes.grid}>
            {["D", "S", "T", "Q", "Q", "S", "S"].map((d, i) => (
              <span key={`dow-${i}`} className={classes.dow}>{d}</span>
            ))}
            {days.map((day, i) => {
              const muted = !isSameMonth(day, viewDate);
              const isSel =
                (start && isSameDay(day, start)) || (end && isSameDay(day, end));
              return (
                <span
                  key={i}
                  className={clsx(
                    classes.day,
                    muted && classes.dayMuted,
                    inRange(day) && classes.dayRange,
                    isSel && classes.daySel
                  )}
                  onClick={() => handleDayClick(day)}
                >
                  {format(day, "d")}
                </span>
              );
            })}
          </div>
        </div>
      </Popover>
    </>
  );
};

export default PeriodFilter;
